import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AppBar, Button, Divider, IconButton, ListItemButton, ListItemIcon, ListItemText, Menu, Toolbar, Tooltip, Typography, useMediaQuery, } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/system";
import { Children, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
/**
 * @component
 * @param {NerdAppBarProps} props NerdAppBar props
 * @param {string} props.title title of the app
 * @param {string} props.logo Logo of the app
 * @param {string} props.variant Size of the appBar (dense or regular)
 * @param {Array<NerdAppBarContent | React.ReactElement>} props.content Height of the appbar. Could be dense or regular.
 * @returns {React.ReactElement | null} UserDisplay component
 */
export default function NerdAppBar({ title, logo, content = [], variant = "dense", appRoot = "/", divider = true, sx, }) {
    const { t } = useTranslation();
    return (_jsxs(AppBar, { position: "fixed", sx: {
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: (theme) => theme.palette.background.default,
            ...sx,
        }, elevation: 0, color: "transparent", children: [_jsxs(Toolbar, { variant: variant, children: [_jsxs(Button, { component: Link, to: appRoot, sx: { color: "text.primary", marginLeft: -2 }, children: [logo != undefined && (_jsx("img", { alt: t("global.logo"), src: logo, height: variant === "dense" ? "30px" : "45px" })), _jsx(Typography, { sx: {
                                    display: { xs: "none", md: "inherit" },
                                    ml: "6px",
                                    fontFamily: "futura-pt-condensed, sans-serif;",
                                    fontSize: 24,
                                }, children: title?.toUpperCase() })] }), Children.toArray(content.map((c) => {
                        if (isNerdAppBarContent(c))
                            return _jsx(NerdAppBarMenuItem, { ...c });
                        return c;
                    }))] }), divider ? _jsx(Divider, {}) : []] }));
}
/**
 *
 * @param {NerdAppBarContent | React.ReactElement} toTest element you want to know if it has the NerdAppBarContent type
 * @returns {boolean} is an NerdAppBarContent or not
 */
function isNerdAppBarContent(toTest) {
    return toTest.title !== undefined;
}
/**
 * @param {NerdAppBarContent} c Content of the menu item
 * @returns {React.ReactElement | null} the menu
 */
export function NerdAppBarMenuItem(c) {
    const theme = useTheme();
    const md = useMediaQuery(theme.breakpoints.up("md"));
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    if (!c) {
        return null;
    }
    let buttonProps = {
        onClick: c.nested ? handleClick : c.callback,
        startIcon: c.startIcon,
        endIcon: c.endIcon,
        variant: c.variant,
        size: c.size,
        sx: c.sx,
    };
    if (c.href) {
        buttonProps = {
            component: Link,
            to: c.href,
        };
    }
    return (_jsxs(_Fragment, { children: [c.divider ? (_jsx(Divider, { sx: { m: 1 }, orientation: "vertical", flexItem: true })) : ([]), md ? (_jsx(Button, { ...buttonProps, children: c.title })) : (_jsx(Tooltip, { title: c.title ? c.title : "", children: _jsx(IconButton, { color: "primary", size: "small", ...buttonProps, children: c.startIcon }) })), c.nested ? (_jsx(StyledMenu, { id: "basic-menu", anchorEl: anchorEl, open: open, onClose: handleClose, anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                }, transformOrigin: {
                    vertical: "top",
                    horizontal: "center",
                }, MenuListProps: {
                    "aria-labelledby": "basic-button",
                }, children: c.nested.map((n, i) => {
                    return (_jsxs(ListItemButton, { component: Link, to: n.href ? n.href : "/", dense: true, divider: n.divider, children: [typeof n.icon === "string" ? (_jsx("div", { className: "faicon", children: _jsx("i", { className: n.icon }) })) : (_jsx(ListItemIcon, { children: n.icon })), _jsx(ListItemText, { primary: n.title, secondary: n.subtitle })] }, i));
                }) })) : ([])] }));
}
export const StyledMenu = styled((props) => (_jsx(Menu, { elevation: 0, anchorOrigin: {
        vertical: "bottom",
        horizontal: "right",
    }, transformOrigin: {
        vertical: "top",
        horizontal: "right",
    }, ...props })))(({ theme }) => ({
    "& .MuiPaper-root": {
        borderRadius: 6,
        marginTop: 10,
        background: theme.palette.mode === "light" ? "white" : theme.palette.grey[900],
        minWidth: 180,
        color: theme.palette.mode === "light"
            ? "rgb(55, 65, 81)"
            : theme.palette.grey[300],
        boxShadow: "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
        "& .MuiMenu-list": {
            padding: "4px 0",
        },
        "& .MuiMenuItem-root": {
            "& .MuiSvgIcon-root": {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            "&:active": {
                backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
            },
        },
    },
}));
